import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, Recycle, Heart } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';

const HERO_IMAGE = '/hero-biolosophy.jpg';
const FABRIC_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/af49a006-9be0-463b-96a6-a9914972e5ea/images/52c599b64c5f46a396cb09aca0b8dc28a111ee61abfacf7fe0bfb7a6bd45272b.png';
const CIRCULAR_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/af49a006-9be0-463b-96a6-a9914972e5ea/images/c40e29fabc547ff13042386a2d95faca5c4f40aa6ec849687ea39daef840801b.jpeg';

export default function HomePage() {
  const { t } = useLang();

  const values = [
    {
      icon: Leaf,
      title: { it: '100% Tessuti Certificati', en: '100% Certified Fabrics' },
      text: {
        it: "L'incontro delle migliori materie prime genera filati di alto pregio e durevoli nel tempo con massima attenzione all'ecosostenibilità, lavorati sapientemente da tecnici e artigiani italiani al fine di ottenere il meglio che la natura può offrirci anche nel vestire. Cotone, lino, canapa, lana, cachemire, alghe, bambù sono elementi naturali selezionati, certificati e curati per creare i capi biolosophy.",
        en: 'The finest raw materials come together to create precious, long-lasting yarns with the utmost attention to eco-sustainability, expertly crafted by Italian technicians and artisans to obtain the best nature can offer us, even in what we wear. Cotton, linen, hemp, wool, cashmere, seaweed and bamboo are natural elements selected, certified and cared for to create biolosophy garments.',
      },
    },
    {
      icon: Recycle,
      title: { it: 'Economia Circolare', en: 'Circular Economy' },
      text: {
        it: "Riporta i tuoi capi e ottieni sconti crescenti. Dopo 9 anni, il nuovo acquisto è gratuito. Siamo talmente sicuri di fornirti capi di assoluta qualità che in qualsiasi momento puoi restituirli per consentire ad altre persone di continuare ad indossarli: il tuo capo reso verrà analizzato, riparato, rigenerato, lavato e trattato con l'ozono per togliere qualsiasi impurità e sarà messo a disposizione nella sezione Pre-Loved con le stesse garanzie del nuovo.",
        en: 'Bring back your garments and earn growing discounts. After 9 years, your new purchase is free. We are so confident in the absolute quality of our garments that you can return them at any time so others can keep wearing them: your returned garment will be analysed, repaired, regenerated, washed and ozone-treated to remove any impurity, and made available in the Pre-Loved section with the same guarantees as new.',
      },
    },
    {
      icon: Heart,
      title: { it: 'Made in Italy', en: 'Made in Italy' },
      text: {
        it: "Ogni capo è realizzato interamente in Italia, dalla produzione dei tessuti alla confezione finale. Per la collezione biolosophy utilizziamo solo tessuti Biojersey, vale a dire materiali certificati da un ente esterno accreditato, lavorati in Italia con maestria ed artigianalità, studiati per raggiungere i maggiori standard qualitativi che consentono al capo di coccolarti per tutto il tempo che vorrai. Questo è il vero Made in Italy… oggi come allora.",
        en: 'Every garment is made entirely in Italy, from fabric production to the final stitch. For the biolosophy collection we use only Biojersey fabrics — materials certified by an accredited external body, crafted in Italy with mastery and artisanship, designed to reach the highest quality standards so your garment can pamper you for as long as you wish. This is true Made in Italy… today as it always was.',
      },
    },
  ];

  const categories = [
    { to: '/shop?category=uomo', testid: 'category-uomo-link', label: { it: 'Uomo', en: 'Men' }, img: 'https://static.prod-images.emergentagent.com/jobs/af49a006-9be0-463b-96a6-a9914972e5ea/images/10a2a83066d81edc43d95258cec1794e96a4797228d3fd7ba2f9d358c2357988.jpeg' },
    { to: '/shop?category=donna', testid: 'category-donna-link', label: { it: 'Donna', en: 'Women' }, img: 'https://static.prod-images.emergentagent.com/jobs/af49a006-9be0-463b-96a6-a9914972e5ea/images/520e517579a1eb0ddf8b2d2c5e2402db0ece0327e59aa54919c91a831532dce3.jpeg' },
    { to: '/shop?category=bambino', testid: 'category-bambino-link', label: { it: 'Bambino', en: 'Kids' }, img: 'https://static.prod-images.emergentagent.com/jobs/af49a006-9be0-463b-96a6-a9914972e5ea/images/10a4a411aa40850283d9e1be827fb2b32414027c5ff415ca9b27c771d64b1efb.jpeg' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Biolosophy Hero"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 55%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-l from-black/70 via-black/30 to-transparent" />
        </div>
        <div className="relative z-10 h-full flex items-center justify-end px-6 md:px-12 lg:px-24 pt-32">
          <div className="max-w-2xl animate-slide-up">
            <h1 className="hero-text-outline text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white mb-6 leading-tight">
              {t({
                it: '.. Se ti dicessi che il tuo capo di abbigliamento tra 9 anni varrà esattamente quanto lo hai pagato? Ci crederesti? ...',
                en: ".. What if I told you that in 9 years your garment will be worth exactly what you paid for it? Would you believe it? ...",
              })}
            </h1>
            <p className="hero-text-outline text-xl md:text-2xl text-white uppercase tracking-widest mb-8">
              {t({ it: 'Benvenuto in biolosophy..', en: 'Welcome to biolosophy..' })}
            </p>
            <Link
              to="/shop"
              data-testid="hero-shop-now-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              {t({ it: 'Entra a far parte del cambiamento', en: 'Become part of the change' })}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F2EFE9]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="overline mb-4">{t({ it: 'I Nostri Valori', en: 'Our Values' })}</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight">
              {t({ it: 'La Filosofia Biolosophy', en: 'The Biolosophy Philosophy' })}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            {values.map((v, i) => (
              <div key={v.title.it} className={`text-center animate-slide-up stagger-${i + 1}`} style={{ opacity: 0 }}>
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#4A5D23] text-white rounded-full">
                  <v.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-medium mb-3">{t(v.title)}</h3>
                <p className="text-[#57534E] leading-relaxed">{t(v.text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Club CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#1C1917] text-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="overline text-[#C05746] mb-4">{t({ it: 'Il Nostro Impegno', en: 'Our Commitment' })}</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-6">
              Biolosophy Value
            </h2>
            <p className="text-white/80 leading-relaxed mb-8">
              {t({
                it: 'Crediamo fermamente che il futuro sia la moda circolare: riporta i tuoi capi biolosophy che non indossi più e ricevi sconti crescenti sui tuoi nuovi acquisti.',
                en: 'We firmly believe the future is circular fashion: bring back the biolosophy garments you no longer wear and earn growing discounts on your new purchases.',
              })}
            </p>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="text-center">
                <div className="text-3xl font-light text-[#4A5D23]">20%</div>
                <div className="text-sm text-white/60">{t({ it: '1° Anno', en: 'Year 1' })}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-light text-[#4A5D23]">50%</div>
                <div className="text-sm text-white/60">{t({ it: '4° Anno', en: 'Year 4' })}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-light text-[#4A5D23]">100%</div>
                <div className="text-sm text-white/60">{t({ it: '9° Anno', en: 'Year 9' })}</div>
              </div>
            </div>
            <Link
              to="/club"
              data-testid="club-cta-btn"
              className="btn-accent inline-flex items-center gap-2"
            >
              {t({ it: 'Scopri il Club Biolosophy', en: 'Discover Club Biolosophy' })}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="relative">
            <img
              src={CIRCULAR_IMAGE}
              alt="Biolosophy Value"
              className="w-full aspect-square object-cover"
            />
          </div>
        </div>
      </section>

      {/* About Fabric */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <img
              src={FABRIC_IMAGE}
              alt={t({ it: 'Tessuti Biologici', en: 'Organic Fabrics' })}
              className="w-full aspect-[4/3] object-cover"
            />
          </div>
          <div className="order-1 lg:order-2">
            <p className="overline mb-4">{t({ it: 'I Nostri Tessuti', en: 'Our Fabrics' })}</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight mb-6">
              {t({ it: 'Biologico dalla Fibra al Capo', en: 'Organic from Fibre to Garment' })}
            </h2>
            <p className="text-[#57534E] leading-relaxed mb-6">
              {t({
                it: "I nostri tessuti biologici nascono dalla passione per la qualità e il rispetto per l'ambiente. Ogni fibra è coltivata senza pesticidi o allevata nel reale rispetto degli animali, lavorata con processi a basso impatto e tinta con coloranti ecosostenibili.",
                en: 'Our organic fabrics are born from a passion for quality and respect for the environment. Every fibre is grown without pesticides or raised with genuine respect for animals, processed with low-impact methods and dyed with eco-sustainable colours.',
              })}
            </p>
            <p className="text-[#57534E] leading-relaxed mb-8">
              {t({
                it: 'Il risultato? Capi morbidi sulla pelle, resistenti nel tempo e gentili con il pianeta. Una scelta consapevole per chi vuole vestire con stile e responsabilità.',
                en: 'The result? Garments that are soft on the skin, built to last and gentle on the planet. A conscious choice for those who want to dress with style and responsibility.',
              })}
            </p>
            <Link
              to="/shop"
              data-testid="about-fabric-shop-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              {t({ it: 'Esplora i Prodotti', en: 'Explore Products' })}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F2EFE9]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="overline mb-4">{t({ it: 'Collezioni', en: 'Collections' })}</p>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight">
              {t({ it: 'Scopri per Categoria', en: 'Shop by Category' })}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {categories.map((cat) => (
              <Link
                key={cat.testid}
                to={cat.to}
                data-testid={cat.testid}
                className="group relative aspect-[3/4] overflow-hidden card-hover"
              >
                <img
                  src={cat.img}
                  alt={t(cat.label)}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-8 left-8">
                  <h3 className="text-2xl text-white font-light">{t(cat.label)}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
